import React, { useRef, useState, useCallback, useMemo } from 'react';
import { View, Text, Pressable, Alert, Platform, ActivityIndicator, TextInput, ScrollView, Modal } from 'react-native';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { YStack, XStack, Separator } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { Card } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AcademyHierarchy, { AcademyHierarchyHandle, ModuleItem } from '@components/Hierarchy/AcademyHierarchy';
import ModuleEditor from '@components/Hierarchy/ModuleEditor';
import { AcademyStackParamList } from '@navigation/types';
import apis from '@api/api';
import LinkGenModal, { DataOptionsModalHandle } from '../DataScreen/CaptureScreen/Components/LinkGenModal';
import globalStyles from '@stylesheet';
import { HOST } from '@api/constant';

type AcademyCourseEditorRouteProp = RouteProp<AcademyStackParamList, 'CourseEditor'>;
type AcademyCourseEditorNavigationProp = StackNavigationProp<AcademyStackParamList, 'CourseEditor'>;

interface CourseModule {
  _id: string;
  title: string;
  description: string;
  content: string;
  order: number;
  duration: number; // in minutes
  type: 'video' | 'text' | 'quiz' | 'assignment';
  completed?: boolean;
  resources?: Array<{
    _id: string;
    title: string;
    url: string;
    type: 'file' | 'link' | 'video';
  }>;
}

interface AcademyCourse {
  _id: string;
  title: string;
  description: string;
  author: string;
  category: string;
  level: string;
  duration: number;
  modules: CourseModule[];
  isPublic: boolean;
  isPublished: boolean;
  thumbnail?: string;
  banner?: string;
}

const AcademyCourseEditor = () => {
  const theme = themeSelector();
  const route = useRoute<AcademyCourseEditorRouteProp>();
  const navigation = useNavigation<AcademyCourseEditorNavigationProp>();
  const academyHierarchyRef = useRef<AcademyHierarchyHandle>(null);
  const linkGenRef = useRef<DataOptionsModalHandle>(null);

  // Module Editor state
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [editingModule, setEditingModule] = useState<ModuleItem | null>(null);

  // Header collapse state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Module Editor handlers
  const handleEditModule = useCallback((module: ModuleItem) => {
    setEditingModule(module);
    setIsEditorVisible(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsEditorVisible(false);
    setEditingModule(null);
  }, []);

  // Get course data from route params
  const params = route.params as AcademyStackParamList['CourseEditor'];
  const { courseId, courseTitle, courseData: passedCourseData, mode = 'edit' } = params || {};
  const isEditMode = mode === 'edit';

  // State for loading (don't show loading for new courses)
  const [isLoading, setIsLoading] = useState(!passedCourseData && !!courseId && courseId !== 'new-course');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Initialize course data from passed data or create empty structure
  const initialCourseData: AcademyCourse = useMemo(() => {
    if (passedCourseData) {
      // Transform passed data to match AcademyCourse structure
      return {
        _id: passedCourseData._id || courseId || 'new-course',
        title: passedCourseData.title || courseTitle || 'New Course',
        description: passedCourseData.description || '',
        author: passedCourseData.author?.profile_information?.name || passedCourseData.author?.email || passedCourseData.author || 'Unknown',
        category: passedCourseData.category || 'General',
        level: passedCourseData.level || 'Beginner',
        duration: passedCourseData.estimatedDuration || 0,
        modules: passedCourseData.modules || [],
        isPublic: passedCourseData.isPublic !== undefined ? passedCourseData.isPublic : true,
        isPublished: passedCourseData.isPublished || false,
        thumbnail: passedCourseData.thumbnail,
        banner: passedCourseData.banner
      };
    }
    // Return empty structure for new course
    return {
      _id: courseId || 'new-course',
      title: courseTitle || 'New Course',
      description: '',
      author: 'You',
      category: 'General',
      level: 'Beginner',
      duration: 0,
      modules: [],
      isPublic: false,
      isPublished: false
    };
  }, [passedCourseData, courseId, courseTitle]);

  // State for managing modules
  const [modules, setModules] = useState<CourseModule[]>(initialCourseData.modules);
  const [courseDataState, setCourseData] = useState<AcademyCourse>(initialCourseData);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  // Course Setup Wizard state
  const [showSetupWizard, setShowSetupWizard] = useState(courseId === 'new-course');
  const [wizardData, setWizardData] = useState({
    title: '',
    description: '',
    category: 'General',
    level: 'Beginner',
    moduleCount: 5,
    isPublic: false
  });
  const [isGenerating, setIsGenerating] = useState(false);

  // Debug wizard state
  React.useEffect(() => {
    console.log('🎓 Course Editor State:', {
      courseId,
      showSetupWizard,
      isLoading,
      isNewCourse: courseId === 'new-course'
    });
  }, [courseId, showSetupWizard, isLoading]);

  // Fetch course data from API if not provided
  React.useEffect(() => {
    const fetchCourseData = async () => {
      if (!passedCourseData && courseId && courseId !== 'new-course') {
        try {
          setIsLoading(true);
          setLoadError(null);
          const response = await apis.getAcademyCourse(courseId);

          if (response.course) {
            const fetchedCourse = response.course;
            const transformedCourse: AcademyCourse = {
              _id: fetchedCourse._id,
              title: fetchedCourse.title,
              description: fetchedCourse.description,
              author: fetchedCourse.author?.profile_information?.name || fetchedCourse.author?.email || 'Unknown',
              category: fetchedCourse.category || 'General',
              level: fetchedCourse.level || 'Beginner',
              duration: fetchedCourse.estimatedDuration || 0,
              modules: fetchedCourse.modules || [],
              isPublic: fetchedCourse.isPublic !== undefined ? fetchedCourse.isPublic : true,
              isPublished: fetchedCourse.isPublished || false,
              thumbnail: fetchedCourse.thumbnail,
              banner: fetchedCourse.banner
            };

            setCourseData(transformedCourse);
            setModules(transformedCourse.modules);
          }
        } catch (error) {
          console.error('Error fetching course data:', error);
          setLoadError('Failed to load course data. Please try again.');
          Alert.alert('Error', 'Failed to load course data. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCourseData();
  }, [courseId, passedCourseData]);

  // Module Editor handlers (moved after modules state declaration)
  const handleSaveModule = useCallback(async (moduleId: string, updatedModule: Partial<ModuleItem>) => {
    try {
      // Update the modules state - convert ModuleItem to CourseModule format
      const updatedModules = modules.map(module => {
        if (module._id === moduleId) {
          // Filter out properties that don't exist in CourseModule
          const { type, ...filteredModule } = updatedModule;
          const updated: CourseModule = {
            ...module,
            ...filteredModule,
            // Ensure duration is a number for CourseModule
            duration: typeof updatedModule.duration === 'string'
              ? parseInt(updatedModule.duration.replace(/[^\d]/g, '')) || 0
              : updatedModule.duration || module.duration,
            // Ensure completed is boolean
            completed: updatedModule.completed !== undefined ? updatedModule.completed : module.completed,
            // Ensure type is valid for CourseModule (exclude 'primary')
            type: (updatedModule.type === 'primary' ? module.type : updatedModule.type) || module.type
          };
          return updated;
        }
        return module;
      });

      setModules(updatedModules);

      // Save to backend if course exists and module content was updated
      if (courseId && courseId !== 'default-course' && updatedModule.content) {
        await apis.updateAcademyModuleContent(courseId, moduleId, updatedModule.content);
      }
    } catch (error) {
      console.error('Error saving module:', error);
      Alert.alert('Error', 'Failed to save module changes. Please try again.');
    }
  }, [modules, courseId]);

  // Handle module position changes
  const handleModulePositionChange = useCallback((moduleId: string, position: { x: number, y: number }) => {
    console.log('📚 Module position changed:', moduleId, position);
    // Here you would save the position to your backend
    // updateModulePosition(moduleId, position);
  }, []);

  // Handle module connections (prerequisites, dependencies)
  const handleModuleConnectionChange = useCallback((sourceId: string, targetId: string, type: string) => {
    console.log('🔗 Module connection created:', sourceId, '->', targetId, type);
    Alert.alert(
      'Module Connection',
      `Module "${modules.find(m => m._id === sourceId)?.title}" is now a prerequisite for "${modules.find(m => m._id === targetId)?.title}"`,
      [{ text: 'OK' }]
    );
  }, [modules]);

  // Handle course creation from wizard
  const handleCreateCourse = useCallback(async () => {
    if (!wizardData.title.trim()) {
      Alert.alert('Required Field', 'Please enter a course title');
      return;
    }

    setIsGenerating(true);
    try {
      // Generate basic modules
      const generatedModules: CourseModule[] = Array.from({ length: wizardData.moduleCount }, (_, i) => ({
        _id: `module_${Date.now()}_${i}`,
        title: `Module ${i + 1}`,
        description: `Content for module ${i + 1}`,
        content: 'Add your content here...',
        order: i + 1,
        duration: 30,
        type: 'text' as const,
        completed: false
      }));

      // Update course data
      const newCourseData: AcademyCourse = {
        _id: 'new-course',
        title: wizardData.title,
        description: wizardData.description,
        author: 'You',
        category: wizardData.category,
        level: wizardData.level,
        duration: wizardData.moduleCount * 30,
        modules: generatedModules,
        isPublic: wizardData.isPublic,
        isPublished: false
      };

      setCourseData(newCourseData);
      setModules(generatedModules);
      setShowSetupWizard(false);
    } catch (error) {
      console.error('Error creating course:', error);
      Alert.alert('Error', 'Failed to create course. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [wizardData]);

  // Handle adding new module
  const handleAddModule = useCallback(() => {
    const newModule: CourseModule = {
      _id: `module_${Date.now()}`,
      title: 'New Module',
      description: 'Click to edit this module',
      content: 'Add your content here...',
      order: modules.length + 1,
      duration: 20,
      type: 'text',
      completed: false
    };

    setModules(prev => [...prev, newModule]);
  }, [modules.length]);


  // Handle deleting module
  const handleDeleteModule = useCallback((moduleId: string) => {
    Alert.alert(
      'Delete Module',
      `Are you sure you want to delete this module?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            setModules(prev => prev.filter(m => m._id !== moduleId));
          }
        }
      ]
    );
  }, []);

  // Handle saving course
  const handleSaveCourse = useCallback(async () => {
    try {
      // Helper function to check if a string is a valid MongoDB ObjectId format
      const isValidObjectId = (id: string): boolean => {
        // MongoDB ObjectId is 24 hex characters
        return /^[0-9a-fA-F]{24}$/.test(id);
      };

      // Convert modules to match AcademyCourse schema
      const academyModules = modules.map((module, index) => {
        // Build module object without _id first
        const moduleData: any = {
          title: module.title,
          description: module.description,
          content: module.content,
          contentType: module.type, // Map type to contentType
          duration: typeof module.duration === 'string'
            ? parseInt((module.duration as string).replace(/[^\d]/g, '')) || 0
            : (module.duration as number) || 0,
          order: index,
          isPublished: true, // Modules are published when course is saved
          resources: module.resources || [],
          prerequisites: [], // Can be enhanced later
          completionCriteria: {
            type: 'view',
            threshold: 100
          },
          metadata: {
            difficulty: 'beginner',
            tags: [],
            estimatedTime: typeof module.duration === 'string'
              ? parseInt((module.duration as string).replace(/[^\d]/g, '')) || 0
              : (module.duration as number) || 0,
            lastModified: new Date()
          }
        };

        // Only include _id if it's a valid ObjectId (not a temporary "module_" ID)
        // Temporary IDs start with "module_" and should be omitted so MongoDB can generate new ObjectIds
        if (module._id && !module._id.startsWith('module_') && isValidObjectId(module._id)) {
          moduleData._id = module._id;
        }
        // If _id starts with "module_" or is invalid, omit it - MongoDB will auto-generate

        return moduleData;
      });

      const academyCourseData = {
        title: courseDataState.title,
        description: courseDataState.description,
        shortDescription: courseDataState.description.substring(0, 500),
        category: courseDataState.category,
        level: courseDataState.level,
        estimatedDuration: modules.reduce((total, module) => {
          const duration = typeof module.duration === 'string'
            ? parseInt((module.duration as string).replace(/[^\d]/g, '')) || 0
            : (module.duration as number) || 0;
          return total + duration;
        }, 0),
        modules: academyModules,
        isPublic: courseDataState.isPublic,
        tags: [],
        prerequisites: [],
        learningObjectives: [],
        targetAudience: [],
        settings: {
          allowEnrollment: true,
          requireApproval: false,
          maxEnrollments: null,
          certificateEnabled: false,
          discussionEnabled: true
        }
      };

      if (courseId && courseId !== 'default-course') {
        // Update existing course
        await apis.updateAcademyCourse(courseId, academyCourseData);
      } else {
        // Create new course
        const result = await apis.createAcademyCourse(academyCourseData);
        // Navigate to the new course
        navigation.navigate('CourseEditor', {
          courseId: result.course._id,
          courseTitle: result.course.title,
          mode: 'edit'
        });
      }
      // TODO THIS MUST ENSURE THAT PAGE PARAMS ARE UPDATED ON SUCCESFUL SAVE
    } catch (error) {
    }
  }, [modules, courseDataState, courseId, navigation]);

  // Handle publishing course
  const handlePublishCourse = useCallback(async () => {
    if (!courseId || courseId === 'default-course') {
      Alert.alert('Error', 'Please save the course first before publishing.');
      return;
    }

    Alert.alert(
      'Publish Course',
      'Are you sure you want to publish this course? It will be visible to all users.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Publish',
          onPress: async () => {
            try {
              // First, ensure the course is set to public
              await apis.updateAcademyCourse(courseId, {
                isPublic: true
              });
              // Then publish the course
              await apis.publishAcademyCourse(courseId);
              Alert.alert('Success', 'Course published successfully!');
              // Update local state
              setCourseData(prev => ({ ...prev, isPublished: true, isPublic: true }));
            } catch (error) {
              console.error('Error publishing course:', error);
              Alert.alert('Error', 'Failed to publish course. Please try again.');
            }
          }
        }
      ]
    );
  }, [courseId]);

  // Handle share course
  const handleShareCourse = useCallback(() => {
    if (!courseId || courseId === 'default-course' || courseId === 'new-course') {
      Alert.alert('Save Required', 'Please save the course first before sharing.');
      return;
    }

    linkGenRef.current?.openModal?.({
      _id: courseDataState._id,
      title: '', // Empty title so URL becomes /en/academy/learn/?id= instead of /en/academy/learn/coursename?id=
      dataTitle: courseDataState.title // Keep the display title for modal
    });
  }, [courseId, courseDataState._id, courseDataState.title]);

  // Loading state
  if (isLoading) {
    return (
      <DefaultScreen
        scrollDisabled
        noMargin
        containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
        style={{ flex: 1, paddingBottom: 0, paddingTop: 0 }}
      >
        <View style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <ActivityIndicator size="large" color={theme ? "#3b82f6" : "#60a5fa"} />
          <Text style={{
            marginTop: 16,
            fontSize: 16,
            color: theme ? "#64748b" : "#94a3b8"
          }}>
            Loading course data...
          </Text>
        </View>
      </DefaultScreen>
    );
  }

  // Error state
  if (loadError) {
    return (
      <DefaultScreen
        scrollDisabled
        noMargin
        containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
        style={{ flex: 1, paddingBottom: 0, paddingTop: 0 }}
      >
        <View style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <MaterialCommunityIcons
            name="alert-circle"
            size={48}
            color="#ef4444"
          />
          <Text style={{
            marginTop: 16,
            fontSize: 16,
            color: "#ef4444",
            textAlign: 'center'
          }}>
            {loadError}
          </Text>
          <Pressable
            onPress={() => navigation.goBack()}
            style={{
              marginTop: 20,
              paddingHorizontal: 24,
              paddingVertical: 12,
              borderRadius: 8,
              backgroundColor: theme ? "#3b82f6" : "#1e40af"
            }}
          >
            <Text style={{ color: 'white', fontWeight: '600' }}>Go Back</Text>
          </Pressable>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <>
      {/* Course Setup Wizard Modal */}
      <Modal
        visible={showSetupWizard}
        animationType="slide"
        transparent={true}
        onRequestClose={() => {
          Alert.alert(
            'Cancel Course Creation',
            'Are you sure you want to cancel? All entered data will be lost.',
            [
              { text: 'Continue Editing', style: 'cancel' },
              { text: 'Cancel', style: 'destructive', onPress: () => navigation.goBack() }
            ]
          );
        }}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
          <View style={{
            backgroundColor: theme ? '#ffffff' : '#1e293b',
            borderRadius: 20,
            padding: 24,
            width: '100%',
            maxWidth: 600,
            maxHeight: '90%',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 12,
            elevation: 10,
          }}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Header */}
              <View style={{ marginBottom: 24 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{
                    fontSize: 24,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9'
                  }}>
                    Create New Course
                  </Text>
                  <Pressable
                    onPress={() => {
                      Alert.alert(
                        'Cancel Course Creation',
                        'Are you sure you want to cancel?',
                        [
                          { text: 'Continue', style: 'cancel' },
                          { text: 'Cancel', style: 'destructive', onPress: () => navigation.goBack() }
                        ]
                      );
                    }}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? '#f1f5f9' : '#334155'
                    }}
                  >
                    <MaterialIcons name="close" size={20} color={theme ? '#64748b' : '#94a3b8'} />
                  </Pressable>
                </View>
                <Text style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8'
                }}>
                  Set up your course details and generate a structure
                </Text>
              </View>

              {/* Course Title */}
              <View style={{ marginBottom: 20 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8
                }}>
                  Course Title *
                </Text>
                <TextInput
                  style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    padding: 16,
                    fontSize: 16,
                    color: theme ? '#1e293b' : '#f1f5f9',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569'
                  }}
                  placeholder="e.g., Introduction to React Native"
                  placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                  value={wizardData.title}
                  onChangeText={(text) => setWizardData(prev => ({ ...prev, title: text }))}
                />
              </View>

              {/* Course Description */}
              <View style={{ marginBottom: 20 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8
                }}>
                  Description
                </Text>
                <TextInput
                  style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    padding: 16,
                    fontSize: 16,
                    color: theme ? '#1e293b' : '#f1f5f9',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569',
                    minHeight: 100,
                    textAlignVertical: 'top'
                  }}
                  placeholder="Describe what students will learn..."
                  placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                  value={wizardData.description}
                  onChangeText={(text) => setWizardData(prev => ({ ...prev, description: text }))}
                  multiline
                  numberOfLines={4}
                />
              </View>

              {/* Category and Level */}
              <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20 }}>
                <View style={{ flex: 1 }}>
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8
                  }}>
                    Category
                  </Text>
                  <View style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569'
                  }}>
                    {['General', 'Marketing', 'AI SaaS', 'Affiliate', 'Automation'].map((cat) => (
                      <Pressable
                        key={cat}
                        onPress={() => setWizardData(prev => ({ ...prev, category: cat }))}
                        style={{
                          padding: 12,
                          borderBottomWidth: cat === 'Automation' ? 0 : 1,
                          borderBottomColor: theme ? '#e2e8f0' : '#475569',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Text style={{
                          fontSize: 14,
                          color: theme ? '#1e293b' : '#f1f5f9'
                        }}>
                          {cat}
                        </Text>
                        {wizardData.category === cat && (
                          <MaterialIcons name="check-circle" size={20} color={theme ? '#3b82f6' : '#60a5fa'} />
                        )}
                      </Pressable>
                    ))}
                  </View>
                </View>

                <View style={{ flex: 1 }}>
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8
                  }}>
                    Level
                  </Text>
                  <View style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569'
                  }}>
                    {['Beginner', 'Intermediate', 'Advanced'].map((lvl) => (
                      <Pressable
                        key={lvl}
                        onPress={() => setWizardData(prev => ({ ...prev, level: lvl }))}
                        style={{
                          padding: 12,
                          borderBottomWidth: lvl === 'Advanced' ? 0 : 1,
                          borderBottomColor: theme ? '#e2e8f0' : '#475569',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Text style={{
                          fontSize: 14,
                          color: theme ? '#1e293b' : '#f1f5f9'
                        }}>
                          {lvl}
                        </Text>
                        {wizardData.level === lvl && (
                          <MaterialIcons name="check-circle" size={20} color={theme ? '#3b82f6' : '#60a5fa'} />
                        )}
                      </Pressable>
                    ))}
                  </View>
                </View>
              </View>

              {/* Module Count */}
              <View style={{ marginBottom: 20 }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8
                }}>
                  Number of Modules
                </Text>
                <TextInput
                  style={{
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    padding: 16,
                    fontSize: 16,
                    color: theme ? '#1e293b' : '#f1f5f9',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569'
                  }}
                  placeholder="5"
                  placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                  value={String(wizardData.moduleCount)}
                  onChangeText={(text) => {
                    const num = parseInt(text) || 0;
                    setWizardData(prev => ({ ...prev, moduleCount: Math.max(1, Math.min(20, num)) }));
                  }}
                  keyboardType="number-pad"
                />
                <Text style={{
                  fontSize: 12,
                  color: theme ? '#64748b' : '#94a3b8',
                  marginTop: 4
                }}>
                  We'll create {wizardData.moduleCount} empty modules for you to customize
                </Text>
              </View>

              {/* Public Toggle */}
              <View style={{ marginBottom: 24 }}>
                <Pressable
                  onPress={() => setWizardData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme ? '#f8fafc' : '#334155',
                    borderRadius: 12,
                    padding: 16,
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#475569'
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4
                    }}>
                      Make Public
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#64748b' : '#94a3b8'
                    }}>
                      Allow all users to view and enroll in this course
                    </Text>
                  </View>
                  <View style={{
                    width: 50,
                    height: 30,
                    borderRadius: 15,
                    backgroundColor: wizardData.isPublic ? (theme ? '#3b82f6' : '#60a5fa') : (theme ? '#e2e8f0' : '#475569'),
                    padding: 2,
                    justifyContent: 'center',
                    alignItems: wizardData.isPublic ? 'flex-end' : 'flex-start'
                  }}>
                    <View style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      backgroundColor: 'white'
                    }} />
                  </View>
                </Pressable>
              </View>

              {/* Action Buttons */}
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <Pressable
                  onPress={() => {
                    Alert.alert(
                      'Cancel Course Creation',
                      'Are you sure you want to cancel?',
                      [
                        { text: 'Continue Editing', style: 'cancel' },
                        { text: 'Cancel', style: 'destructive', onPress: () => navigation.goBack() }
                      ]
                    );
                  }}
                  style={{
                    flex: 1,
                    padding: 16,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#64748b' : '#94a3b8'
                  }}>
                    Cancel
                  </Text>
                </Pressable>

                <Pressable
                  onPress={handleCreateCourse}
                  disabled={isGenerating || !wizardData.title.trim()}
                  style={{
                    flex: 1,
                    padding: 16,
                    borderRadius: 12,
                    backgroundColor: (isGenerating || !wizardData.title.trim())
                      ? (theme ? '#cbd5e1' : '#475569')
                      : (theme ? '#3b82f6' : '#1e40af'),
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  {isGenerating ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <MaterialIcons name="auto-fix-high" size={20} color="white" />
                  )}
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: 'white'
                  }}>
                    {isGenerating ? 'Creating...' : 'Create Course'}
                  </Text>
                </Pressable>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <DefaultScreen
        scrollDisabled
        noMargin
        containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
        style={{ flex: 1, top: -30, paddingBottom: 0, paddingTop: 0 }}
      >
        {/* Header */}
        <Card
        style={{
          margin: 20,
          marginBottom: 10,
          borderRadius: 16,
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 12,
          elevation: 6,
        }}
          pointerEvents={Platform.OS !== 'web' ? 'box-none' : undefined}
      >
          <YStack padding={20} pointerEvents={Platform.OS !== 'web' ? 'auto' : undefined}>
          {/* Collapsed Header - Minimal View */}
          {isHeaderCollapsed ? (
            <XStack alignItems="center" justifyContent="space-between">
              <XStack alignItems="center" gap={12} flex={1}>
                <Text style={{
                  fontSize: 18,
                  fontWeight: '700',
                  color: theme ? "#1e293b" : "#f1f5f9",
                }}>
                  {courseDataState.title}
                </Text>

                {/* Quick Stats in Collapsed Mode */}
                <XStack alignItems="center" gap={12}>
                  <XStack alignItems="center" gap={4}>
                    <MaterialCommunityIcons
                      name="book-open"
                      size={14}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                    <Text style={{
                      fontSize: 11,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {modules.length}
                    </Text>
                  </XStack>

                  <XStack alignItems="center" gap={4}>
                    <MaterialCommunityIcons
                      name="clock"
                      size={14}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                    <Text style={{
                      fontSize: 11,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {courseDataState.duration}m
                    </Text>
                  </XStack>
                </XStack>

                {/* Mode Badge */}
                <View style={{
                  backgroundColor: isEditMode
                    ? (theme ? "#ddd6fe" : "#312e81")
                    : (theme ? "#dbeafe" : "#1e40af"),
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 16,
                }}>
                  <Text style={{
                    fontSize: 10,
                    fontWeight: '600',
                    color: isEditMode
                      ? (theme ? "#7c3aed" : "#a78bfa")
                      : (theme ? "#1e40af" : "#93c5fd")
                  }}>
                    {isEditMode ? 'EDIT' : 'LEARN'}
                  </Text>
                </View>
              </XStack>

              {/* Expand Button */}
              <Pressable
                onPress={() => setIsHeaderCollapsed(false)}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? "#f1f5f9" : "#334155",
                }}
              >
                <MaterialCommunityIcons
                  name="chevron-down"
                  size={20}
                  color={theme ? "#64748b" : "#94a3b8"}
                />
              </Pressable>
            </XStack>
          ) : (
            <>
              {/* Expanded Header - Full View */}
              <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
                <YStack flex={1}>
                  <Text style={{
                    fontSize: 24,
                    fontWeight: '700',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 4
                  }}>
                      {courseDataState.title}
                    </Text>
                    <Text style={{
                      fontSize: 14,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {courseDataState.description}
                    </Text>
                  </YStack>

                  <XStack alignItems="center" gap={8}>
                    {/* Mode Badge */}
                    <View style={{
                      backgroundColor: isEditMode
                        ? (theme ? "#ddd6fe" : "#312e81")
                        : (theme ? "#dbeafe" : "#1e40af"),
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      borderRadius: 20,
                    }}>
                      <Text style={{
                        fontSize: 12,
                        fontWeight: '600',
                        color: isEditMode
                          ? (theme ? "#7c3aed" : "#a78bfa")
                          : (theme ? "#1e40af" : "#93c5fd")
                      }}>
                        {isEditMode ? 'EDIT MODE' : 'LEARNING MODE'}
                      </Text>
                    </View>

                    {/* Collapse Button */}
                    <Pressable
                      onPress={() => setIsHeaderCollapsed(true)}
                      style={{
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                      }}
                    >
                      <MaterialCommunityIcons
                        name="chevron-up"
                        size={20}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                    </Pressable>
                  </XStack>
                </XStack>

                {/* Action Buttons */}
                <XStack gap={12} flexWrap="wrap">
                  {isEditMode && (
                    <><Pressable
                      onPress={() => navigation.goBack()}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                        paddingHorizontal: 16,
                        paddingVertical: 10,
                        borderRadius: 8,
                        gap: 8
                      }}
                    >
                      <MaterialCommunityIcons
                        name="arrow-left"
                        size={16}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? "#64748b" : "#94a3b8"
                      }}>
                        Back
                      </Text>
                    </Pressable>
                      <Pressable
                        onPress={handleAddModule}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: theme ? "#dbeafe" : "#1e40af",
                          paddingHorizontal: 16,
                          paddingVertical: 10,
                          borderRadius: 8,
                          gap: 8
                        }}
                      >
                        <MaterialCommunityIcons
                          name="plus"
                          size={16}
                          color={theme ? "#1e40af" : "#93c5fd"}
                        />
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: theme ? "#1e40af" : "#93c5fd"
                        }}>
                          Add Module
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={handleSaveCourse}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: theme ? "#d1fae5" : "#064e3b",
                          paddingHorizontal: 16,
                          paddingVertical: 10,
                          borderRadius: 8,
                          gap: 8
                        }}
                      >
                        <MaterialCommunityIcons
                          name="content-save"
                          size={16}
                          color={theme ? "#059669" : "#10b981"}
                        />
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: theme ? "#059669" : "#10b981"
                        }}>
                          Save Course
                        </Text>
                      </Pressable>

                      <Pressable
                        onPress={handlePublishCourse}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: theme ? "#fef3c7" : "#78350f",
                          paddingHorizontal: 16,
                          paddingVertical: 10,
                          borderRadius: 8,
                          gap: 8
                        }}
                      >
                        <MaterialCommunityIcons
                          name="publish"
                          size={16}
                          color={theme ? "#d97706" : "#f59e0b"}
                        />
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '600',
                          color: theme ? "#d97706" : "#f59e0b"
                        }}>
                          Publish
                        </Text>
                      </Pressable>

                        <Pressable
                          onPress={handleShareCourse}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            backgroundColor: theme ? "#ddd6fe" : "#4c1d95",
                            paddingHorizontal: 16,
                            paddingVertical: 10,
                            borderRadius: 8,
                            gap: 8
                          }}
                        >
                          <MaterialCommunityIcons
                            name="share-variant"
                            size={16}
                            color={theme ? "#7c3aed" : "#a78bfa"}
                          />
                          <Text style={{
                            fontSize: 14,
                            fontWeight: '600',
                            color: theme ? "#7c3aed" : "#a78bfa"
                          }}>
                            Share
                          </Text>
                        </Pressable>
                    </>
                  )}

                  
                </XStack>

                {/* Course Stats */}
                <XStack gap={20} marginTop={16} flexWrap="wrap">
                  <XStack alignItems="center" gap={6}>
                    <MaterialCommunityIcons
                      name="book-open"
                      size={16}
                      color={theme ? "#3b82f6" : "#60a5fa"}
                    />
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {modules.length} modules
                    </Text>
                  </XStack>

                  <XStack alignItems="center" gap={6}>
                    <MaterialCommunityIcons
                      name="clock"
                      size={16}
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {courseDataState.duration} min total
                    </Text>
                  </XStack>

                  <XStack alignItems="center" gap={6}>
                    <MaterialCommunityIcons
                      name="account"
                      size={16}
                      color={theme ? "#7c3aed" : "#a78bfa"}
                    />
                    <Text style={{
                      fontSize: 12,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {courseDataState.level} level
                    </Text>
                  </XStack>
                </XStack>
            </>
          )}
        </YStack>
      </Card>

      <Separator borderColor="white" />

      {/* Hierarchy Editor */}
        <View
          style={{
            flex: 1,
            alignSelf: 'stretch',
            ...(Platform.OS !== 'web' && { pointerEvents: 'auto' } as any)
          }}
          pointerEvents={Platform.OS !== 'web' ? 'auto' : undefined}
        >
        <AcademyHierarchy
          ref={academyHierarchyRef}
          courseId={courseId || 'default-course'}
          courseTitle={courseDataState.title}
          courseDescription={courseDataState.description}
          initialModules={modules.map((mod, index) => {
            // Calculate hierarchical position for modules with proper component dimensions
            const componentWidth = 280;
            const componentHeight = 160;

            // Center the primary course in the viewport (4000x4000)
            const primaryX = 2000 - (componentWidth / 2); // Center horizontally
            const primaryY = 2000 - (componentHeight / 2); // Center vertically

            // Organize modules in levels to the right with proper spacing
            const modulesPerLevel = 4; // Maximum modules per level
            const levelSpacing = componentWidth + 100; // Horizontal spacing between levels (card width + gap)
            const moduleSpacing = componentHeight + 50; // Vertical spacing between modules in same level (card height + gap)

            const level = Math.floor(index / modulesPerLevel);
            const positionInLevel = index % modulesPerLevel;

            // Calculate position relative to primary course
            const levelX = primaryX + levelSpacing + (level * levelSpacing);
            const levelY = primaryY + (positionInLevel - (modulesPerLevel - 1) / 2) * moduleSpacing;

            return {
              _id: mod._id,
              title: mod.title,
              description: mod.description,
              type: mod.type as 'video' | 'text' | 'quiz' | 'assignment',
              content: mod.content,
              duration: `${mod.duration} min`,
              level: courseDataState.level,
              isCompleted: mod.completed || false,
              x: levelX,
              y: levelY,
            };
          })}
          mode={mode}
          onModulePositionChange={handleModulePositionChange}
          onModuleConnectionChange={handleModuleConnectionChange}
          onModuleEdit={handleEditModule}
          onModuleDelete={handleDeleteModule}
        />
      </View>

        {/* Module Editor - Full Screen Overlay */}
        <ModuleEditor
          isVisible={isEditorVisible}
          module={editingModule}
          onClose={handleCloseEditor}
          onSave={handleSaveModule}
        />

        {/* Link Generation Modal */}
        <LinkGenModal
          ref={linkGenRef}
          idOnly
          url={`${HOST}/en/academy/learn/`}
          heading="Share Course"
          caption="Share this course link with students to allow them to enroll and start learning."
        />
      </DefaultScreen>
    </>
  );
};

export default AcademyCourseEditor;
